import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { navItems, lineUrl } from "@/mocks/siteData";

interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Set<string>>(new Set());
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleMobileSub = useCallback((label: string) => {
    setMobileExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === "/articles" && location.pathname.startsWith("/articles/")) return true;
    if (path === "/testimonials" && location.pathname.startsWith("/stories")) return true;
    return false;
  };

  const isChildActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some((child) => location.pathname === child.path || location.pathname.startsWith(child.path + "/"));
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-abyss-900/95 backdrop-blur-xl border-b border-cyber-gold/20 shadow-lg shadow-cyber-gold/5"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg border border-cyber-gold/40 bg-abyss-800/60 group-hover:border-cyber-gold/70 transition-colors">
                <i className="ri-cpu-line text-cyber-gold text-lg" />
              </div>
              <div className="flex flex-col">
                <span className="text-cyber-gold font-bold text-base leading-tight tracking-wide">
                  時光逆行駭客
                </span>
                <span className="font-mono text-base text-neon-blue tracking-widest leading-tight">
                  CHRONOHACKER
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
              {navItems.map((item: NavItem) => {
                const hasChildren = !!item.children && item.children.length > 0;
                const open = activeDropdown === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasChildren && setActiveDropdown(item.label)}
                    onMouseLeave={() => hasChildren && setActiveDropdown(null)}
                  >
                    <Link
                      to={item.path}
                      className={`relative flex items-center gap-1 px-3 py-2 nav-desktop-link whitespace-nowrap transition-all duration-300 rounded-md ${
                        isActive(item.path) || isChildActive(item)
                          ? "text-cyber-gold bg-cyber-gold/10"
                          : "text-ghost hover:text-cyber-gold hover:bg-ghost/5"
                      }`}
                    >
                      {item.label}
                      {hasChildren && (
                        <i
                          className={`ri-arrow-down-s-line text-base transition-transform ${
                            open ? "rotate-180" : ""
                          }`}
                        />
                      )}
                      {(isActive(item.path) || isChildActive(item)) && (
                        <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-cyber-gold rounded-full" />
                      )}
                    </Link>
                    {hasChildren && open && (
                      <div className="absolute top-full left-0 pt-2">
                        <div className="bg-abyss-700 border border-cyber-gold/20 rounded-xl shadow-xl shadow-black/40 overflow-hidden min-w-[200px]">
                          {item.children?.map((child) => (
                            <Link
                              key={child.label}
                              to={child.path}
                              className="flex items-center gap-2 px-4 py-3 text-base text-ghost-muted hover:text-cyber-gold hover:bg-cyber-gold/5 transition-colors border-l-2 border-transparent hover:border-cyber-gold"
                            >
                              <i className="ri-arrow-right-s-line text-cyber-gold/60 text-base" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-base px-4 py-2.5"
              >
                <i className="ri-line-fill" />
                加入 LINE 諮詢
              </a>
              <Link to="/booking" className="btn-gold-outline text-base px-4 py-2.5">
                預約逆齡評估
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-cyber-gold"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="開啟選單"
            >
              <div className="flex flex-col gap-1.5">
                <span
                  className={`block w-6 h-px bg-cyber-gold transition-all ${
                    mobileOpen ? "rotate-45 translate-y-1" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-px bg-cyber-gold transition-all ${
                    mobileOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-px bg-cyber-gold transition-all ${
                    mobileOpen ? "-rotate-45 -translate-y-1.5" : ""
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-abyss-900/98 backdrop-blur-xl transition-all duration-500 lg:hidden ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col h-full pt-20 pb-8 px-6 overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {navItems.map((item: NavItem) => {
              const hasChildren = !!item.children && item.children.length > 0;
              const expanded = mobileExpanded.has(item.label);
              return (
                <div key={item.label} className="border-b border-cyber-gold/10">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.path}
                      className={`flex-1 py-4 nav-mobile-link transition-colors rounded-md px-3 ${
                        isActive(item.path) || isChildActive(item)
                          ? "text-cyber-gold bg-cyber-gold/10"
                          : "text-ghost"
                      }`}
                      onClick={() => !hasChildren && setMobileOpen(false)}
                    >
                      {item.label}
                    </Link>
                    {hasChildren && (
                      <button
                        className="w-10 h-10 flex items-center justify-center text-cyber-gold/60"
                        onClick={() => toggleMobileSub(item.label)}
                        aria-label="展開子選單"
                      >
                        <i
                          className={`ri-arrow-down-s-line text-lg transition-transform ${
                            expanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {hasChildren && expanded && (
                    <div className="flex flex-col gap-1 pl-4 pb-3">
                      {item.children?.map((child) => (
                        <Link
                          key={child.label}
                          to={child.path}
                          className="flex items-center gap-2 py-2 text-base text-ghost-muted hover:text-cyber-gold transition-colors"
                          onClick={() => setMobileOpen(false)}
                        >
                          <i className="ri-arrow-right-s-line text-cyber-gold/40 text-base" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="mt-auto flex flex-col gap-3 pt-6">
            <a
              href={lineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-line w-full justify-center"
            >
              <i className="ri-line-fill" />
              加入 LINE 諮詢
            </a>
            <Link to="/booking" className="btn-gold-outline w-full justify-center">
              預約逆齡評估
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
